import React from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";

import { Button, formLayout, IconTrash, Input } from "@busbud/horizon";

import { useAppContext } from "@app/helpers/hooks";
import type { UserBillingAddress } from "@app/types/napi-types";

import type { SubmitHandler } from "react-hook-form";

import { clickedUpdateBillingAddress } from "../../../../../../tracking/account-tracking";
import { updateUserBillingAddressState } from "../../../../state/user-billing-address";
import { CountryField } from "../fields/country-field";
import { StateField } from "../fields/state-field";
import { ZipCodeField } from "../fields/zip-code-field";

function getInitialValues(user_billing_address: UserBillingAddress | null) {
  return {
    street_line: user_billing_address?.street_line || "",
    city: user_billing_address?.city || "",
    zip_code: user_billing_address?.zip_code || "",
    state: user_billing_address?.state || "",
    country_code: user_billing_address?.country_code || ""
  };
}

export interface OwnProps {
  onError: (error: string) => void;
  onSuccess: () => void;
  onDelete: () => void;
}

interface ReduxProps {
  user_billing_address: UserBillingAddress | null;
  user_id: number;
}

export const BillingAddressForm: React.FC<OwnProps & ReduxProps> = ({
  user_billing_address,
  user_id,
  onError,
  onSuccess,
  onDelete
}) => {
  const methods = useForm({
    mode: "onChange",
    defaultValues: getInitialValues(user_billing_address)
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid, isSubmitting }
  } = methods;

  const { tracker, translator, api } = useAppContext();
  const dispatch = useDispatch();

  const onSubmit: SubmitHandler<any> = values => {
    tracker.track(clickedUpdateBillingAddress());
    api
      .saveUserBillingAddress(user_id, values)
      .then(response => {
        dispatch(updateUserBillingAddressState(response));
        reset(values);
        onSuccess();
      })
      .catch(() =>
        onError(translator.t("!accounts.settings.save-billing-address-error"))
      );
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={formLayout.fields.responsive}
      >
        <Input
          className="col-span-4"
          data-testid="street_line"
          labelText={translator.t("!checkout.payment.billing-address")}
          isInvalid={!!errors.street_line}
          autoComplete="street-address"
          helperText={translator.t("!checkout.payment.billing-address.tooltip")}
          {...register("street_line", { required: true })}
          isRequired
        />

        <Input
          className="col-span-4 sm:col-span-3"
          data-testid="city"
          labelText={translator.t("!checkout.payment.city")}
          isInvalid={!!errors.city}
          autoComplete="city"
          isRequired
          {...register("city", { required: true })}
        />

        <ZipCodeField className="col-span-4 sm:col-span-1" />

        <CountryField className="col-span-4 sm:col-span-2" />

        <StateField className="col-span-4 sm:col-span-2" />

        {!!isDirty && (
          <Button
            data-testid="submit"
            id="submit"
            isSubmit
            isDisabled={isSubmitting || !isValid}
            variant="contained"
            appearance="primary"
            size="lg"
            className="col-span-4 w-full content-center"
          >
            {translator.t("!button.save")}
          </Button>
        )}

        <Button
          data-testid="delete"
          id="delete"
          size="lg"
          variant="contained"
          appearance="destructive"
          className="col-span-4 w-full content-center"
          iconStart={<IconTrash size="md" />}
          onClick={() => onDelete()}
        >
          {translator.t("!accounts.settings.delete-address")}
        </Button>
      </form>
    </FormProvider>
  );
};
